import { computed, ref, watch } from 'vue'
import { filterGuildPlayers } from '../lib/guildPlayerFilter'

import { useApollo, useGuild } from '@/shared/ports'
import { isHistoricalRaid, raidStatus as deriveRaidStatus } from '../lib/raidCalendar'
import {
  FOREST_LOCATIONS,
  forestSpawnSlots as buildForestSpawnSlots,
} from '../lib/gvgMap'
import { resolveLivePlayerForRaidPlayer } from '../lib/raidPlayerLiveLink'
import {
  applyPlannerState,
  PlannerStateChangedDocument,
} from './liveSync'
import type { PlannerStateChangedSubscription } from './liveSync'
import {
  handleForbiddenLiveSyncPayload,
  isForbiddenLiveSyncError,
  useLiveAccessRevocation,
} from './liveAccessRevocation'
import type {
  GroupId,
  Player,
  PlayerTag,
  RaidStatus,
  RaidSummary,
  RoomId,
} from '../types'
import { useRaidPlayerBuildsActions } from './raid/playerBuilds'
import { useRaidMap } from './raid/map'
import {
  raidGroupToGroup,
  raidPlayerToPlayer,
  raidRoomToRoom,
} from './raid/mappers'
import { useGvgPlannerStoreCore } from './gvgPlannerStoreCore'

export function useGvgPlannerStoreComposition() {
  const core = useGvgPlannerStoreCore()
  const {
    board,
    raid,
    tag,
    buildPreset,
    session,
    loading,
    error,
    liveSyncError,
    subscription,
    liveSnapshotGeneration,
    runners,
    numericRoomId,
    numericGroupId,
    numericRaidId,
    fetchBoardApi,
    raidLifecycleActions,
    locale,
  } = core

  /**
   * Live-board players keyed by `discordUserId`. Powers the FX-05 builds
   * chip fallback for raid snapshot rows whose per-snapshot `builds` are
   * empty (the raid was just created / the player never promoted a build
   * into it yet) — without this map, `raidPlayerBuildsActions.setActiveBuild`
   * would reject every click on a freshly-snapshotted raid card. Built as a
   * computed so the underlying `board.players` ref change re-derives it
   * without a manual refresh.
   */
  const livePlayersByDiscordId = computed<ReadonlyMap<string, Player>>(
    () => new Map(board.players.value.map((player) => [player.discordUserId, player])),
  )

  function buildIndexForWeapons(
    builds: ReadonlyArray<ReadonlyArray<string>>,
    weapons: ReadonlyArray<string>,
  ): number {
    return builds.findIndex((build) =>
      build.length === weapons.length && build.every((slug, index) => slug === weapons[index]),
    )
  }

  const raidPlayerBuildsActions = useRaidPlayerBuildsActions({
    activeDiscordGuildId: session.activeDiscordGuildId,
    error,
    activeRaidId: raid.activeRaidId,
    activeStageId: raid.activeStageId,
    raidPlayers: raid.raidPlayers,
    livePlayersByDiscordId,
    runReporting: runners.runReporting,
  })

  const raidMap = useRaidMap({
    activeDiscordGuildId: session.activeDiscordGuildId,
    activeRaidId: raid.activeRaidId,
  })
  const forestSpawnSlotsComputed = computed(() =>
    buildForestSpawnSlots(
      raid.activeRaidBoard.value?.raid.scheduledStartsAt ?? null,
      raid.activeRaidBoard.value?.raid.durationMinutes,
    ),
  )
  watch(raid.activeRaidId, (raidId) => {
    if (raidId === null) {
      raidMap.clearRaidMapPlan()
      return
    }
    void raidMap.fetchRaidMapPlan()
  })

  // ── getters (board-only, raid-aware) ──────────────────────────────────
  const playerById = computed(() => (id: string) => currentPlayers.value.find((p) => p.id === id))
  const groupById = computed(() => (id: GroupId) => currentGroups.value.find((g) => g.id === id))
  const playersInGroup = computed(() => (groupId: GroupId) => {
    const g = currentGroups.value.find((x) => x.id === groupId)
    if (!g) return [] as ReadonlyArray<never>
    return g.playerIds
      .map((id) => currentPlayers.value.find((p) => p.id === id))
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
  })
  const roomGroups = computed(() => (roomId: RoomId) => {
    const r = currentRooms.value.find((x) => x.id === roomId)
    if (!r) return [] as ReadonlyArray<never>
    return r.groupIds
      .map((id) => currentGroups.value.find((g) => g.id === id))
      .filter((g): g is NonNullable<typeof g> => Boolean(g))
  })
  const roomWeaponCounts = computed(() => (roomId: RoomId) => {
    const groupIdSet = new Set<GroupId>(roomGroups.value(roomId).map((g) => g.id))
    const counts: Record<string, number> = {}
    for (const player of currentPlayers.value) {
      if (player.group === null || !groupIdSet.has(player.group)) continue
      for (const slug of player.mainWeapons) {
        if (slug === '') continue
        counts[slug] = (counts[slug] ?? 0) + 1
      }
    }
    return counts
  })
  const counts = computed(() => {
    const knownGroupIds = new Set<GroupId>(currentGroups.value.map((g) => g.id))
    let assigned = 0
    for (const p of currentPlayers.value) {
      if (p.group !== null && knownGroupIds.has(p.group)) assigned += 1
    }
    return {
      players: currentPlayers.value.length,
      groups: currentGroups.value.length,
      rooms: currentRooms.value.length,
      assigned,
      orphans: currentPlayers.value.length - assigned,
    }
  })

  // `numericRoomId` is populated alongside the live-board response, but it
  // is a plain Map. Derive a reactive view from the rooms ref so raid rooms
  // rejoin their legacy live-room FKs when that concurrent response lands.
  const liveRoomIds = computed<ReadonlyMap<string, number>>(() =>
    new Map(board.rooms.value.map((room) => [room.id, numericRoomId.get(room.id) ?? Number(room.id)])),
  )

  // Raid-aware mirrors (live vs raid snapshot).
  const currentRooms = computed(() => {
    if (raid.activeRaidId.value === null) return board.rooms.value
    return raid.raidRooms.value.map((rr) =>
      raidRoomToRoom(rr, raid.raidGroups.value, liveRoomIds.value, raid.raidRooms.value),
    )
  })
  const currentGroups = computed(() => {
    const groups = raid.activeRaidId.value === null
      ? board.groups.value
      : raid.raidGroups.value.map(raidGroupToGroup)
    if (board.guildPlayerDiscordIds.value === null) return groups
    const visibleIds = new Set(currentPlayers.value.map((player) => player.id))
    return groups.map((group) => ({
      ...group,
      playerIds: group.playerIds.filter((id) => visibleIds.has(id)),
    }))
  })
  const currentPlayers = computed(() => {
    if (raid.activeRaidId.value === null) {
      return filterGuildPlayers(board.players.value, board.guildPlayerDiscordIds.value)
    }
    return filterGuildPlayers(raid.raidPlayers.value.map((rp) => {
      const livePlayer = resolveLivePlayerForRaidPlayer(
        rp,
        board.players.value,
        livePlayersByDiscordId.value,
      )
      // The selector always displays the current personal library. Raid-local
      // rows remain in `rp.builds` for persistence, while the action layer
      // merges the selected personal or guild pair into that snapshot set.
      const builds: ReadonlyArray<ReadonlyArray<string>> = livePlayer?.builds ?? rp.builds
      const activeBuildIndex = buildIndexForWeapons(builds, rp.mainWeapons)
      const activeGuildBuild = activeBuildIndex < 0
        ? livePlayer?.guildBuilds.find((entry) =>
          buildIndexForWeapons([entry.weaponSlugs], rp.mainWeapons) === 0,
        )
        : undefined
      return {
        ...raidPlayerToPlayer(rp, builds, raid.raidGroups.value),
        // Guild-build entries remain live player metadata. Tags come from
        // `raidPlayerToPlayer(rp, ...)`, whose assignments belong to this
        // snapshot and can diverge from both the live board and cloned raids.
        guildBuilds: livePlayer?.guildBuilds ?? [],
        // Exactly one row is marked active. A matching personal pair takes
        // precedence when the same two weapons exist in both libraries.
        activeBuildIndex,
        activeGuildBuildIndex: activeGuildBuild?.orderIndex ?? -1,
      }
    }), board.guildPlayerDiscordIds.value)
  })

  const tagById = computed(
    () => (id: string) =>
      tag.playerTags.value.find((t) => t.id === id) as PlayerTag | undefined,
  )

  const tagsForPlayer = computed(() => (playerId: string) => {
    const p = board.players.value.find((x) => x.id === playerId)
    if (!p) return [] as ReadonlyArray<PlayerTag>
    return p.tags
  })
  const playerCountForTag = computed(() => (tagId: string) => {
    let count = 0
    for (const p of board.players.value) {
      if (p.tags.some((t) => t.id === tagId)) count += 1
    }
    return count
  })

  const raidStatusFn = (r: RaidSummary): RaidStatus => deriveRaidStatus(r, new Date())
  const activeRaidEditNow = ref<number>(Date.now())
  const refreshActiveRaidEditLock = (now: Date = new Date()): void => {
    activeRaidEditNow.value = now.getTime()
  }
  const activeRaidEditLocked = computed<boolean>(() => {
    const activeRaid = raid.activeRaidBoard.value?.raid
    return activeRaid !== undefined
      && !session.canEditHistoricalRaids.value
      && isHistoricalRaid(activeRaid, new Date(activeRaidEditNow.value))
  })

  // ── boardChanged subscription watcher ─────────────────────────────
  // The selected raid/stage are subscription variables because different
  // tabs may render different snapshots of the same guild. Re-subscribing
  // keeps each event's hydrated state scoped to its active planner view.
  const guildPort = useGuild()
  const apollo = useApollo()
  const plannerStateRefs = {
    guildPlayerDiscordIds: board.guildPlayerDiscordIds,
    board: {
      players: board.players,
      groups: board.groups,
      rooms: board.rooms,
      numericRoomId,
      numericGroupId,
    },
    buildPresets: {
      applyLibrary: buildPreset.applyLibrary,
      clearLibrary: buildPreset.clearLibrary,
    },
    loading,
    invalidateBoardFetch: fetchBoardApi.invalidateFetch,
    playerTags: tag.playerTags,
    activeTagFilter: tag.activeTagFilter,
    raids: raid.raids,
    numericRaidId,
    calendarWindowStart: raid.calendarWindowStart,
    raidsForWindow: raid.raidsForWindow,
    activeRaidId: raid.activeRaidId,
    activeStageId: raid.activeStageId,
    raidBoard: {
      raidRooms: raid.raidRooms,
      raidGroups: raid.raidGroups,
      raidPlayers: raid.raidPlayers,
      activeRaidBoard: raid.activeRaidBoard,
    },
    raidStages: raid.raidStages,
    raidStageDiffs: raid.raidStageDiffs,
    stageSelectionPending: raid.stageSelectionPending,
    applyRaidMapPlan: raidMap.applyRaidMapPlan,
    locale,
    clearRaidMapPlan: raidMap.clearRaidMapPlan,
  }
  const liveAccessRevocation = useLiveAccessRevocation({
    subscription,
    liveSyncError,
    liveSnapshotGeneration,
    clearSelectedRaid: () => raidLifecycleActions.selectRaid(null),
    plannerStateRefs,
    guild: guildPort,
  })

  watch(
    [guildPort.dbGuildId, raid.activeRaidId, raid.activeStageId],
    ([id, activeRaidId, activeStageId]) => {
      subscription.value?.unsubscribe()
      subscription.value = null
      liveSyncError.value = null
      if (id === null) return
      const numericId = Number(id)
      if (!Number.isFinite(numericId)) return
      const selectedRaidId = activeRaidId === null ? null : Number(activeRaidId)
      const selectedStageId = activeStageId === null ? null : Number(activeStageId)
      if (
        (selectedRaidId !== null && !Number.isFinite(selectedRaidId))
        || (selectedStageId !== null && !Number.isFinite(selectedStageId))
      ) {
        return
      }
      const obs = apollo.subscribe<PlannerStateChangedSubscription>({
        query: PlannerStateChangedDocument,
        fetchPolicy: 'no-cache',
        variables: {
          guildId: numericId,
          raidId: selectedRaidId,
          stageId: selectedStageId,
        },
      })
      subscription.value = obs.subscribe({
        next: (event) => {
          if (handleForbiddenLiveSyncPayload(event, liveAccessRevocation)) return
          const state = event.data?.boardChanged.state
          if (state === null || state === undefined) {
            liveSyncError.value = 'A live planner event arrived without state. Waiting for the next update.'
            return
          }
          const applied = applyPlannerState(
            plannerStateRefs,
            state,
          )
          if (!applied) return
          liveSnapshotGeneration.value += 1
          liveSyncError.value = null
        },
        error: (e: unknown) => {
          if (isForbiddenLiveSyncError(e)) {
            void liveAccessRevocation.handle()
            return
          }
          liveSyncError.value = e instanceof Error ? e.message : String(e)
        },
      })
    },
    { immediate: true },
  )

  /**
   * ISS-07 — view-side stale guard for the WS-error banner.
   */
  function clearLiveSyncError(): void {
    liveSyncError.value = null
  }

  return {
    ...core,
    livePlayersByDiscordId,
    buildIndexForWeapons,
    raidPlayerBuildsActions,
    raidMap,
    forestSpawnSlotsComputed,
    playerById,
    groupById,
    playersInGroup,
    roomGroups,
    roomWeaponCounts,
    counts,
    liveRoomIds,
    currentRooms,
    currentGroups,
    currentPlayers,
    tagById,
    tagsForPlayer,
    playerCountForTag,
    raidStatusFn,
    activeRaidEditLocked,
    refreshActiveRaidEditLock,
    guildPort,
    apollo,
    plannerStateRefs,
    liveAccessRevocation,
    clearLiveSyncError,
    FOREST_LOCATIONS,
    raidPlayerToPlayer,
    raidRoomToRoom,
    raidGroupToGroup,
  }
}

export type GvgPlannerStoreComposition = ReturnType<
  typeof useGvgPlannerStoreComposition
>
